import React, { useState, useCallback, useEffect, useRef } from 'react';
import { trackNewsView, trackNewsClick } from '../services/analytics';
import './CardNews.css';

const CardNews = ({ nouveautes }) => {
  const [loadedImages, setLoadedImages] = useState({});
  const cardRefs = useRef({});
  const trackedNewsRef = useRef(new Set()); // Éviter les doublons
  const observerRef = useRef(null);

  const handleImageLoad = useCallback((id) => {
    setLoadedImages(prev => ({
      ...prev,
      [id]: true
    }));
  }, []);

  const handleCardClick = (newsItem) => {
    const { id, titre, categorie, urlRedirection } = newsItem;
    
    // Tracker le clic en arrière-plan (non bloquant)
    trackNewsClick(id, titre, categorie, urlRedirection).catch(() => {
      // Erreur silencieuse
    });
    
    // Redirection immédiate (ne pas attendre le tracking)
    if (urlRedirection) {
      const fullUrl = urlRedirection.startsWith('http') 
        ? urlRedirection 
        : `https://${urlRedirection}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Tracking des vues avec Intersection Observer
  useEffect(() => {
    if (!nouveautes || nouveautes.length === 0) return;

    // Nettoyer l'observer précédent
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    const observerOptions = {
      root: null,                    // Viewport
      rootMargin: '0px',
      threshold: 0.3                  // 30% de visibilité minimum
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const newsId = entry.target.dataset.newsId;
          
          // Vérifier qu'on n'a pas déjà tracké cette nouveauté
          if (newsId && !trackedNewsRef.current.has(newsId)) {
            // Marquer immédiatement comme tracké (synchrone)
            trackedNewsRef.current.add(newsId);
            
            const news = nouveautes.find(n => n.id === newsId);
            if (news) {
              // Tracker en arrière-plan (non bloquant)
              trackNewsView(
                news.id,
                news.titre || 'Sans titre',
                news.categorie || 'unknown'
              ).catch(() => {
                // Erreur silencieuse - déjà gérée par trackError
              });
            }
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    observerRef.current = observer;
    
    const observedRefs = new Set();

    const observeCards = () => {
      let observedCount = 0;
      Object.values(cardRefs.current).forEach((ref) => {
        if (ref && !observedRefs.has(ref)) {
          observer.observe(ref);
          observedRefs.add(ref);
          observedCount++;
        }
      });
      
      // Réessayer si aucune carte n'a été observée
      if (observedCount === 0 && nouveautes.length > 0) {
        setTimeout(observeCards, 200);
      }
    };

    // Attendre que le DOM soit prêt
    requestAnimationFrame(() => {
      setTimeout(observeCards, 50);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [nouveautes]);

  const isNewItem = (timestamp) => {
    if (!timestamp) return false;
    const now = new Date();
    const itemDate = new Date(timestamp);
    const diffTime = Math.abs(now - itemDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7; // Considéré comme nouveau pendant 7 jours
  };

  return (
    <div className="news-cards-container">
      {nouveautes.length === 0 ? (
        <div className="news-empty-state">
          <p>Aucune nouveauté n'est disponible pour le moment.</p>
        </div>
      ) : (
        nouveautes.map((item, index) => (
          <article 
            key={item.id} 
            ref={(el) => { cardRefs.current[item.id] = el; }}
            data-news-id={item.id}
            className="card-news"
          >
            {isNewItem(item.timestamp) && (
              <div className="card-news__badge">Nouveauté</div>
            )}
            <div className="card-news__category-tag">
              {item.categorie}
            </div>

            <div className="card-news__image-container">
              <img
                src={item.imageUrl}
                alt={item.titre}
                className={`card-news__image ${loadedImages[item.id] ? 'loaded' : ''}`}
                loading="lazy"
                decoding="async"
                fetchPriority={index < 2 ? "high" : "low"}
                onLoad={() => handleImageLoad(item.id)}
                width="100%"
                height="auto"
              />
              {!loadedImages[item.id] && (
                <div className="card-news__image-placeholder">
                  {/* Suppression du rond de chargement */}
                </div>
              )}
            </div>
            
            <div className="card-news__content">
              <h3 className="card-news__title">{item.titre}</h3>
              <p className="card-news__description">{item.description}</p>
              {item.urlRedirection && (
                <button 
                  className="card-news__button"
                  onClick={() => handleCardClick(item)}
                >
                  Ouvrir le lien
                </button>
              )}
            </div>
          </article>
        ))
      )}
    </div>
  );
};

export default CardNews; 